/**
 * rodape.ts — Categoria "Rodapé" (10 modelos premium)
 *
 * Fechamentos de página com marca, links, aviso legal e variações com
 * chamada para ação, contato e faixa escura.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote C)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { WEB, b } from './_shared';

const LINKS = 'Política de privacidade|#\nTermos de uso|#\nContato|#formulario';

/** Atalho para o rodapé. */
function rodape(props: Record<string, unknown> = {}) {
  return b('page.footer', {
    brand: 'Sua Empresa',
    text: 'Soluções sob medida para o seu negócio.',
    links: LINKS,
    legal: '© Sua Empresa. Todos os direitos reservados.',
    ...props,
  });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'rodape.simples',
    category: 'rodape',
    label: 'Rodapé simples',
    description: 'Marca, frase curta e aviso legal.',
    preview: 'footer',
    outputs: WEB,
    order: 1,
    build: () => [rodape({ links: '' })],
  },
  {
    key: 'rodape.com-links',
    category: 'rodape',
    label: 'Rodapé com links',
    description: 'Links institucionais e aviso legal.',
    preview: 'footer',
    outputs: WEB,
    order: 2,
    build: () => [rodape()],
  },
  {
    key: 'rodape.escuro',
    category: 'rodape',
    label: 'Rodapé escuro',
    description: 'Fundo escuro com texto claro.',
    preview: 'footer',
    outputs: WEB,
    order: 3,
    build: () => [rodape({ backgroundColor: '#0f172a', textColor: '#e2e8f0' })],
  },
  {
    key: 'rodape.com-cnpj',
    category: 'rodape',
    label: 'Rodapé com CNPJ',
    description: 'Aviso legal completo, com dados da empresa.',
    preview: 'footer',
    outputs: WEB,
    order: 4,
    build: () => [
      rodape({
        text: 'Atendimento de segunda a sexta, das 8h às 18h.',
        legal: '© Sua Empresa. CNPJ 00.000.000/0001-00 — Av. Paulista, 1000, São Paulo/SP',
        backgroundColor: '#0f172a',
        textColor: '#e2e8f0',
      }),
    ],
  },
  {
    key: 'rodape.com-convite',
    category: 'rodape',
    label: 'Convite antes do rodapé',
    description: 'Chamada para ação seguida do rodapé.',
    preview: 'footer',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '860px', backgroundColor: '#f8fafc', motion: 'fade' }, [
        b('ui.title', { text: 'Ainda dá tempo de falar com a gente', size: 'md', level: 'h2', align: 'center' }),
        b('ui.buttons', { items: 'Falar agora|#formulario|solid', align: 'center' }),
      ]),
      rodape(),
    ],
  },
  {
    key: 'rodape.com-contato',
    category: 'rodape',
    label: 'Contato antes do rodapé',
    description: 'Telefone, e-mail e endereço acima do fechamento.',
    preview: 'footer',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '1080px' }, [
        b('page.contact', {
          title: 'Fale com a gente',
          columns: '3',
          items: 'Telefone|(11) 4000-0000\nE-mail|contato@empresa.com.br\nEndereço|Av. Paulista, 1000 — São Paulo/SP',
        }),
      ]),
      rodape({ backgroundColor: '#0f172a', textColor: '#e2e8f0' }),
    ],
  },
  {
    key: 'rodape.claro',
    category: 'rodape',
    label: 'Rodapé em cinza claro',
    description: 'Fechamento discreto para páginas claras.',
    preview: 'footer',
    outputs: WEB,
    order: 7,
    build: () => [rodape({ backgroundColor: '#f8fafc', textColor: '#334155' })],
  },
  {
    key: 'rodape.com-selo',
    category: 'rodape',
    label: 'Rodapé com selo',
    description: 'Selo de segurança acima do rodapé.',
    preview: 'footer',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'sm', align: 'center', maxWidth: '900px' }, [
        b('ui.badge', { text: 'Dados protegidos conforme a LGPD', variant: 'outline', align: 'center' }),
      ]),
      rodape(),
    ],
  },
  {
    key: 'rodape.minimo',
    category: 'rodape',
    label: 'Rodapé mínimo',
    description: 'Apenas o aviso legal, sem links.',
    preview: 'footer',
    outputs: WEB,
    order: 9,
    build: () => [rodape({ text: '', links: '' })],
  },
  {
    key: 'rodape.completo',
    category: 'rodape',
    label: 'Rodapé completo',
    description: 'Convite, contato e fechamento escuro.',
    preview: 'footer',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Vamos conversar?', size: 'md', level: 'h2', align: 'center' }),
        b('page.contact', {
          title: '',
          columns: '3',
          items: 'Telefone|(11) 4000-0000\nE-mail|contato@empresa.com.br\nWhatsApp|(11) 90000-0000',
        }),
        b('ui.buttons', { items: 'Pedir proposta|#formulario|solid', align: 'center' }),
      ]),
      rodape({ backgroundColor: '#0f172a', textColor: '#e2e8f0' }),
    ],
  },
];

registerPatterns(modelos);
